// Interjections — the short bursts that carry emotion: reactions, fillers,
// and conversational sounds. Grouped reference lists sit at the bottom.
import { renderGuideSections } from "../ui/guideSections.js";

const GROUPS = {
  "Reactions & emotion": [
    ["Ahi!", "Ouch!"], ["Uffa!", "Ugh! / Argh!"], ["Caspita!", "Wow! / Good grief!"],
    ["Che bello!", "Great! / How nice!"], ["Macché!", "No way!"], ["Mamma mia!", "Oh my goodness!"],
    ["Accidenti!", "Darn! / Wow!"], ["Magari!", "I wish! / If only!"],
  ],
  "Agreement & encouragement": [
    ["Certo!", "Of course!"], ["Va bene!", "OK! / All right!"], ["Dai!", "Come on!"],
    ["Forza!", "Go on! / Come on!"], ["Esatto!", "That's it!"], ["Coraggio!", "Cheer up! / You can do it!"],
  ],
  "Fillers & hesitation": [
    ["Beh…", "Well…"], ["Allora…", "So… / Well…"], ["Insomma…", "Well… / In short…"],
    ["Cioè…", "I mean…"], ["Ecco…", "Here / So…"], ["Sai?", "You know?"],
  ],
  "Greetings & politeness": [
    ["Ciao!", "Hi! / Bye!"], ["Ehi! / Senti!", "Hey! (listen)"], ["Salute! / Cin cin!", "Bless you! / Cheers!"],
    ["Aiuto!", "Help!"], ["Attento! / Occhio!", "Watch out! (lit. eye)"], ["Basta!", "Enough!"],
  ],
};

export function interjectionsView(app) {
  const block = (title, rows) => `
    <div class="bg-white rounded-2xl border border-sage-100 p-5">
      <h3 class="text-lg font-bold text-sage-800 mb-3">${title}</h3>
      <div class="overflow-x-auto"><table class="w-full text-sm">
        <thead><tr class="text-left text-xs uppercase tracking-wide text-sage-500 border-b border-sage-100">
          <th class="py-2 pr-3">Italian</th><th class="py-2 px-3">English</th>
        </tr></thead>
        <tbody>
          ${rows
            .map(
              ([es, en]) => `<tr class="border-b border-sage-50 last:border-0">
            <td class="py-2 pr-3 font-medium text-sage-800">${es}</td>
            <td class="py-2 px-3 text-sage-700">${en}</td>
          </tr>`
            )
            .join("")}
        </tbody>
      </table></div>
    </div>`;

  const table = `
    <div class="grid sm:grid-cols-2 gap-4 mt-6">
      ${Object.entries(GROUPS)
        .map(([title, rows]) => block(title, rows))
        .join("")}
    </div>`;

  renderGuideSections(
    app,
    "Interjections",
    "Interjections are the short exclamations that express emotion or reaction — and the little fillers that keep a conversation flowing. They're grammatically independent, so they don't change form; you just learn them whole.",
    [
      {
        title: "1. How they work",
        body: `
          <p class="text-sage-700 mb-2">An interjection stands on its own — it isn't part of the sentence's grammar. In writing it's simply followed by an exclamation mark (Italian has no upside-down opener):</p>
          <ul class="text-sage-700 text-sm space-y-1 list-disc pl-5">
            <li><i><b>Ahi!</b> Mi sono fatto male.</i> (Ouch! I've hurt myself.)</li>
            <li><i><b>Caspita!</b> Non lo sapevo.</i> (Wow! I didn't know.)</li>
          </ul>
        `,
      },
      {
        title: "2. Fillers buy you thinking time",
        body: `
          <p class="text-sage-700 mb-2">Natural speech is full of small fillers. Sprinkling these in makes you sound far more fluent than perfect grammar alone:</p>
          <ul class="text-sage-700 text-sm space-y-1 list-disc pl-5">
            <li><i><b>Beh</b>, non lo so…</i> (Well, I don't know…)</li>
            <li><i><b>Allora</b>, la verità è che…</i> (So, the truth is…)</li>
            <li><i><b>Cioè</b>, quello che voglio dire è…</i> (I mean, what I want to say is…)</li>
            <li><i><b>Ecco</b>…</i> (Here / So…)</li>
          </ul>
        `,
      },
      {
        title: "3. Watch the register",
        body: `
          <p class="text-sage-700 text-sm">Many interjections are colloquial. <i>Dai!</i> and <i>Boh!</i> (“dunno”) are everywhere in casual Italian; <i>Magari!</i> swings between “maybe” and a wistful “I wish!”. Some exclamations are mild swears — fine among friends, risky in formal settings. When in doubt, the neutral picks are <i>Certo!</i>, <i>Che bello!</i>, and <i>Caspita!</i>.</p>
        `,
      },
    ],
    "",
    table
  );
  window.scrollTo({ top: 0, behavior: "smooth" });
}
